import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { AVATARS, LOGO } from "@/data/exercises";
import GamesBrowser from "@/components/patient/GamesBrowser";
import { GroupGamePlayer, GroupGamesList } from "@/components/group/GroupGamePlayer";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Users, Play, Loader2, Check } from "lucide-react";

export default function GroupSession() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [present, setPresent] = useState([]);
  const [step, setStep] = useState("select"); // select | play
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("group");
  const [groupGame, setGroupGame] = useState(null);

  const load = useCallback(() => {
    api.get(`/groups/${groupId}`)
      .then((r) => { setGroup(r.data); setPresent(r.data.members.map((m) => m.id)); })
      .catch(() => navigate("/tableau-de-bord"))
      .finally(() => setLoading(false));
  }, [groupId, navigate]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) =>
    setPresent((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const recordGame = async (meta) => {
    try {
      await api.post("/group-sessions", {
        group_id: groupId, participant_ids: present,
        category: meta.category, exercise_id: meta.exercise_id, exercise_name: meta.exercise_name,
        score: meta.score, accuracy: meta.accuracy, duration_seconds: meta.duration, points: meta.points || {},
      });
      toast.success(`Séance enregistrée pour ${present.length} résident(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement de la séance impossible.");
    }
  };

  const finishGroupGame = async ({ game, points, group: total, duration }) => {
    if (total === 0 && duration < 10) { setGroupGame(null); return; }
    await recordGame({ category: "groupe", exercise_id: `group-${game.type}`, exercise_name: game.title, score: total, accuracy: 0, duration, points });
    setGroupGame(null);
  };

  if (loading || !group) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]"><Loader2 className="w-10 h-10 animate-spin text-emerald-700" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <header className="flex items-center justify-between p-5 sm:p-7">
        <button onClick={() => (step === "play" ? setStep("select") : navigate("/tableau-de-bord"))}
          data-testid="group-session-back"
          className="flex items-center gap-2 h-14 px-5 rounded-2xl bg-white border-4 border-emerald-700/20 font-patient font-bold text-lg text-gray-700 active:scale-95 transition-transform">
          <ChevronLeft className="w-6 h-6" /> {step === "play" ? "Changer les présents" : "Retour"}
        </button>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 font-patient font-bold text-lg text-emerald-800">
            <Users className="w-5 h-5" /> {group.name}
          </span>
          <img src={LOGO} alt="Passerelle" className="h-9 w-auto" />
        </div>
      </header>

      <main className="flex-1 p-6 pb-16">
        {step === "select" ? (
          <div className="max-w-2xl mx-auto soft-in" data-testid="group-select-participants">
            <h1 className="font-serif-display text-3xl sm:text-4xl font-normal text-gray-800 mb-2">Séance de groupe — {group.name}</h1>
            <p className="text-xl font-patient text-gray-500 mb-8">Qui est présent aujourd'hui ? Touchez pour sélectionner.</p>

            {group.members.length === 0 ? (
              <div className="rounded-3xl bg-white border-4 border-dashed border-gray-300 p-10 text-center">
                <p className="text-xl font-patient font-bold text-gray-400">Ce groupe n'a pas encore de résidents. Ajoutez-en depuis le tableau de bord.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
                {group.members.map((m) => {
                  const on = present.includes(m.id);
                  return (
                    <button key={m.id} onClick={() => toggle(m.id)} data-testid={`group-member-${m.id}`}
                      className={`relative rounded-3xl p-5 flex flex-col items-center gap-3 border-4 transition-colors ${on ? "bg-emerald-50 border-emerald-500" : "bg-white border-gray-200"}`}>
                      {on && <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center"><Check className="w-4 h-4" /></span>}
                      <img src={m.avatar || AVATARS[0]} alt={m.name} className="w-20 h-20 rounded-full object-cover border-4 border-amber-200" />
                      <span className="font-patient font-black text-lg text-gray-800 text-center">{m.name}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <Button onClick={() => setStep("play")} disabled={present.length === 0}
              data-testid="group-start-button"
              className="w-full h-16 rounded-2xl text-white font-patient font-black text-2xl"
              style={{ background: "#2E8EC2" }}>
              <Play className="w-7 h-7 mr-2" /> Démarrer la séance ({present.length})
            </Button>
          </div>
        ) : (
          <div className={`${mode === "group" ? "max-w-6xl" : "max-w-2xl"} mx-auto soft-in`} data-testid="group-play">
            <div className="mb-6 rounded-2xl bg-emerald-50 border-2 border-emerald-200 px-5 py-3 flex items-center gap-2 flex-wrap" data-testid="group-present-bar">
              <Users className="w-5 h-5 text-emerald-700" />
              <span className="font-patient font-bold text-emerald-800">Présents :</span>
              {group.members.filter((m) => present.includes(m.id)).map((m) => (
                <span key={m.id} className="px-3 h-8 rounded-full bg-white border border-emerald-200 text-emerald-800 font-semibold text-sm inline-flex items-center">{m.name}</span>
              ))}
            </div>
            {!groupGame && (
              <div className="flex gap-3 mb-6" data-testid="group-mode-tabs">
                {[["group", "Jeux de groupe"], ["solo", "Jeux individuels"]].map(([id, label]) => (
                  <button key={id} onClick={() => setMode(id)} data-testid={`group-mode-${id}`}
                    className={`h-14 px-6 rounded-2xl font-patient font-black text-xl border-4 transition-colors ${mode === id ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-gray-700 border-gray-200"}`}>{label}</button>
                ))}
              </div>
            )}
            {mode === "group" ? (
              groupGame
                ? <GroupGamePlayer game={groupGame} players={group.members.filter((m) => present.includes(m.id))} onFinish={finishGroupGame} />
                : <GroupGamesList onPick={setGroupGame} players={present.length} />
            ) : <GamesBrowser onGameComplete={recordGame} />}
          </div>
        )}
      </main>
    </div>
  );
}
